import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";

import { dataFaq } from "@data/index";
import {
  QUERY_WIDTHS,
  getBrowserVersion,
  requestCallSchema,
} from "@utils/index";
import { RequestCallFormInterface } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { useUserRequestCall } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Drawer,
  DrawerHeader,
  XSvg,
  Form,
  FormRow,
  FormInput,
  FormText,
  Button,
  ArrowUpRightSvg,
} from "@components/index";

export const RequestCall = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const phone = useUserStore(({ phone }) => phone);
  const businesses = useUserStore(({ businesses }) => businesses);
  const userId = useUserStore(({ userId }) => userId);
  const [browserVersion] = useState(getBrowserVersion);

  const userBusinessTypes = useMemo(
    () => businesses.map(({ type }) => type),
    [businesses],
  );

  const { mutate: mutateRequestCall, isPending: isPendingRequestCall } =
    useUserRequestCall(
      () => {
        handleClose();
        notify("success", "Your message has been submitted.");
      },
      ({ message, fields }) => {
        Object.keys(fields || {}).forEach((key) =>
          setError(key as "phone" | "message" | "userId" | "browserVersion", {
            message: fields![key],
          }),
        );
        notify("error", message);
      },
    );

  useEffect(() => {
    setState({ isLoading: isPendingRequestCall });
  }, [isPendingRequestCall, setState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    getValues,
    setError,
  } = useForm<RequestCallFormInterface>({
    mode: "onBlur",
    disabled: isPendingRequestCall,
    resolver: yupResolver(requestCallSchema),
  });

  useEffect(() => {
    if (show) {
      reset({ phone });
    }
  }, [show, phone, reset]);

  const handleClose = () => {
    setState({ isDrawerShown: false });
    reset();
    onClose();
  };

  const onSubmit = () => {
    const data = getValues();
    mutateRequestCall({ ...data, userId, browserVersion });
  };

  return (
    show && (
      <Drawer>
        <DrawerHeader
          onClose={handleClose}
          title={
            <div>
              <div className="text-300 font-bold text-black-100 md:text-600">
                Request a Call
              </div>
              {!isMobile && (
                <div className="w-[380px] pt-1 text-300 text-black-54">
                  Please check our FAQ section before submitting the form, as it
                  may already answer your question.
                </div>
              )}
            </div>
          }
        />
        {isMobile && (
          <div className="px-4 pt-8 text-black-54">
            Please check our FAQ section before submitting the form, as it may
            already answer your question.
          </div>
        )}
        <Form
          className="flex max-h-[calc(100dvh_-_122px)] flex-col justify-between overflow-y-auto px-4 text-black-100 md:max-h-[calc(100dvh_-_156px)] md:px-14"
          onSubmit={handleSubmit(onSubmit)}
        >
          <input
            type="hidden"
            value={userId}
            {...register<"userId">("userId")}
          />
          <input
            type="hidden"
            value={browserVersion}
            {...register<"browserVersion">("browserVersion")}
          />
          <div>
            <FormRow top={isMobile ? "medium" : "large"}>
              <FormInput
                {...register<"phone">("phone")}
                type="phone"
                label="Phone number"
                error={formState.errors.phone?.message}
                onChange={() => clearErrors("phone")}
              />
            </FormRow>
            <FormRow top={isMobile ? "small" : "large"}>
              <FormText
                {...register<"message">("message")}
                name="message"
                label="Message"
                error={formState.errors.message?.message}
                onChange={() => clearErrors("message")}
              />
            </FormRow>
            <div className="pt-6 md:pt-10">
              <div className="pb-5 text-300 font-bold text-black-100 md:text-400">
                FAQ's
              </div>
              <div className="max-h-[calc(100dvh_-_622px)] overflow-y-auto md:max-h-[calc(100dvh_-_715px)]">
                {dataFaq.map(({ category, businessTypes }) =>
                  !businessTypes?.length ||
                  userBusinessTypes.find((type) =>
                    businessTypes?.includes(type),
                  ) ? (
                    <a
                      key={category}
                      href={`/faq#${category}`}
                      target="_blank"
                      className="flex justify-between border-b border-b-black-16 py-4 text-200 font-normal *:text-black-54 last:border-0 md:border-b-2 md:border-b-black-8 md:text-300 md:font-bold"
                    >
                      {category}
                      <ArrowUpRightSvg />
                    </a>
                  ) : null,
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-y-4 border-t-0 border-t-black-8 pb-2 md:-mx-14 md:flex-row md:justify-between md:border-t md:px-14 md:pb-4 md:pt-4">
            <Button type="submit" title="Submit" height="small" />
            <Button
              variant="secondary"
              leftIcon={<XSvg />}
              title="Cancel"
              height="small"
              onClick={handleClose}
            />
          </div>
        </Form>
      </Drawer>
    )
  );
};
